import React, { createContext, useContext, useMemo, useState } from 'react';

import { useGetPaymentTokens } from '../hooks';
import { getFirstToken } from '../helper/common';

import { BOMPaymentToken, CardPaymentToken, PayWith } from '../type';
import { DeleteTokenPayload, IPaymentContext, SelectedToken } from './type';

export const initContextValue = {
	tokenLoading: true,
	defaultMethod: PayWith.CARD,
	refreshTokens: () => {},
	tokens: { BOM: { tokens: [], defaultToken: undefined }, Cards: { defaultToken: undefined, tokens: [] } },

	// common payment state
	onDeleteToken: () => {},
	closeDeleteModal: () => {},
	openDeleteConfirmationModal: () => {},

	// Card Payment state
	onCardSelect: () => {},
	onAddCard: () => {},

	// Bank Alias state
	onBankAliasSelect: () => {},
	onAddBankAlias: () => {},
};

export const PaymentContext = createContext<IPaymentContext>(initContextValue);

export const usePaymentMethod = () => {
	const context = useContext(PaymentContext);

	if (!context) {
		throw new Error('usePaymentMethod must be used within a PaymentMethodProvider');
	}

	return context;
};
// PaymentMethodProvider component
export const PaymentMethodProvider: React.FC = ({ children }) => {
	// Get payment tokens and methods
	const { BOM, cards, defaultMethod, loading, refresh } = useGetPaymentTokens();

	// State for managing token deletion
	const [deleteTokenPayload, setDeleteTokenPayload] = useState<DeleteTokenPayload>();

	// State for managing selected tokens
	const [selectedToken, setSelectedToken] = useState<SelectedToken>({
		BOM: BOM.defaultToken,
		card: cards.defaultToken,
	});

	// State for unsaved tokens
	const [unSavedCards, setUnSavedCards] = useState<CardPaymentToken[]>([]);
	const [unSavedBankAlias, setUnSavedBankAlias] = useState<BOMPaymentToken[]>([]);

	// Update selected card token
	const onCardSelect = (card: CardPaymentToken | undefined) => setSelectedToken((prev) => ({ ...prev, card }));

	// Update selected bank alias token
	const onBankAliasSelect = (BOM: BOMPaymentToken | undefined) => setSelectedToken((prev) => ({ ...prev, BOM }));

	// Add a new card and update the state
	const onAddCard = (token: CardPaymentToken) => {
		setUnSavedCards((prev) => [token, ...prev]);
		onCardSelect(token);
	};

	// Add a new bank alias and update the state
	const onAddBankAlias = (token: BOMPaymentToken) => {
		setUnSavedBankAlias((prev) => [token, ...prev]);
		onBankAliasSelect(token);
	};

	// Open confirmation modal for token deletion
	const openDeleteConfirmationModal = (tokenID: string, tokenType: PayWith, isUnsaved: boolean) => {
		setDeleteTokenPayload({ tokenID, tokenType, isUnsaved });
	};

	// Remove a card token
	const removeCard = () => {
		if (!deleteTokenPayload) return;
		if (deleteTokenPayload.tokenID === selectedToken.card?.id) {
			onCardSelect(undefined);
		}
		setUnSavedCards((prev) => prev.filter(({ id }) => id !== deleteTokenPayload.tokenID));
	};

	// Remove a bank alias token
	const removeBankAlias = () => {
		if (!deleteTokenPayload) return;
		if (deleteTokenPayload.tokenID === selectedToken.BOM?.id) {
			onBankAliasSelect(undefined);
		}
		setUnSavedBankAlias((prev) => prev.filter(({ id }) => id !== deleteTokenPayload.tokenID));
	};

	// Delete the token based on its type
	const onDeleteToken = () => {
		if (deleteTokenPayload?.tokenType === PayWith.CARD) {
			removeCard();
		} else {
			removeBankAlias();
		}
		closeDeleteModal();
	};

	// Close the delete modal
	const closeDeleteModal = () => setDeleteTokenPayload(undefined);

	// Memoized card tokens with unsaved cards included
	const filterCardTokens = useMemo(() => {
		const _savedTokens = cards.tokens.slice().sort((a, b) => b.daysTillExpiry - a.daysTillExpiry);
		return [...unSavedCards, ..._savedTokens];
	}, [cards.tokens, unSavedCards]);

	// Memoized BOM tokens with unsaved bank aliases included
	const filterBOMTokens = useMemo(() => [...unSavedBankAlias, ...BOM.tokens], [BOM.tokens, unSavedBankAlias]);

	// Memoized context value
	const memorizedContentValue = useMemo(
		() => ({
			defaultMethod,
			tokenLoading: loading,
			refreshTokens: refresh,
			tokens: {
				BOM: {
					defaultToken: selectedToken.BOM ?? BOM.defaultToken ?? getFirstToken(unSavedBankAlias),
					tokens: filterBOMTokens,
				},
				Cards: {
					defaultToken: selectedToken.card ?? cards.defaultToken ?? getFirstToken(unSavedCards),
					tokens: filterCardTokens,
				},
			},
			closeDeleteModal,
			deleteTokenPayload,
			onAddCard,
			onCardSelect,
			openDeleteConfirmationModal,
			onDeleteToken,
			onAddBankAlias,
			onBankAliasSelect,
		}),
		[BOM, cards, selectedToken, loading, defaultMethod, deleteTokenPayload, filterBOMTokens, filterCardTokens]
	);

	return <PaymentContext.Provider value={memorizedContentValue}>{children}</PaymentContext.Provider>;
};
